import { useMemo, useState } from "react";
import { ContactSearchList } from "./ContactSearchList";
import type { Contact } from "../lib/types";

/** Multi-select search picker over ``processed_contacts``.
 *
 * Used by the authoring-start form to build positive / negative example sets
 * by id instead of pasting Apollo IDs by hand. Selection is controlled — the
 * parent owns the array of picked contacts and renders the submit button.
 * The picker collapses behind a trigger button so the form stays compact;
 * selected chips stay visible whether the search list is open or not.
 */
export function ContactMultiPicker({
  selected,
  onToggle,
  onRemove,
  accentColor,
  placeholder,
}: {
  selected: Contact[];
  onToggle: (contact: Contact) => void;
  onRemove: (contactId: string) => void;
  accentColor?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const selectedIds = useMemo(
    () => new Set(selected.map((contact) => contact.id)),
    [selected],
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          className="disclosure-trigger"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
        >
          <span aria-hidden style={{ display: "inline-block", width: 10 }}>
            {open ? "▾" : "▸"}
          </span>
          Pick contacts
          {selected.length > 0 && <span>· {selected.length}</span>}
        </button>
        {selected.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {selected.map((contact) => (
              <span
                key={contact.id}
                className="chip"
                style={{
                  background: accentColor ?? "var(--accent-blue)",
                  color: "white",
                  cursor: "pointer",
                }}
                onClick={() => onRemove(contact.id)}
                title="Click to remove"
              >
                {contact.company || contact.domain} ✕
              </span>
            ))}
          </div>
        )}
      </div>
      {open && (
        <div style={{ marginTop: 8 }}>
          <ContactSearchList
            placeholder={placeholder}
            itemAction={(contact) => (
              <button type="button" onClick={() => onToggle(contact)}>
                {selectedIds.has(contact.id) ? "Remove" : "Add"}
              </button>
            )}
          />
        </div>
      )}
    </div>
  );
}
